import { useMemo, useState } from 'react'

import { Box, Button, Flex, Group, Stack, Stepper, Text, Title, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconArrowNarrowLeft } from '@tabler/icons-react'
import { AxiosError } from 'axios'
import { useGetOne, useNotify, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { usePostApiVideoPromotionOperations } from '../../codegen/generated/video-promotion'
import {
  selectEndscreenConfig,
  selectInfocardsConfig,
  useVideoPromotionStore,
} from '../../stores/useVideoPromotionStore'
import { YoutubeChannel } from '../../types/YoutubeChannel'
import { NewPromotionConfirmation } from './NewPromotionSteps/NewPromotionConfirmation'
import { NewPromotionElementsConfiguration } from './NewPromotionSteps/NewPromotionElementsConfiguration'
import { NewPromotionVideosSelect } from './NewPromotionSteps/NewPromotionVideosSelect'

const APP_BAR_HEIGHT = 48

export const NewPromotionOperation = () => {
  const t = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()
  const videoPromotionStore = useVideoPromotionStore()

  const [active, setActive] = useState(0)
  const nextStep = () => setActive(current => (current < 2 ? current + 1 : current))
  const prevStep = () => setActive(current => (current > 0 ? current - 1 : current))

  const [name, setName] = useState('')
  const [overwriteExistingEndscreen, setOverwriteExistingEndscreen] = useState(true)
  const [overwriteExistingInfocards, setOverwriteExistingInfocards] = useState(true)

  const createOperationMutation = usePostApiVideoPromotionOperations()

  const endScreenConfig = useMemo(
    () => selectEndscreenConfig(videoPromotionStore),
    [videoPromotionStore],
  )
  const infocardsConfig = useMemo(
    () => selectInfocardsConfig(videoPromotionStore),
    [videoPromotionStore],
  )

  const youtubeChannelQuery = useGetOne<YoutubeChannel>(
    'youtubeChannels',
    { id: videoPromotionStore.youtubeChannelId ?? '' },
    { enabled: !!videoPromotionStore.youtubeChannelId },
  )

  const onConfirmLaunch = () => {
    const elements = [
      endScreenConfig && t('pages.videoPromotion.endScreen._title'),
      infocardsConfig && t('pages.videoPromotion.infoCards._title'),
    ]
      .filter(Boolean)
      .join(' + ')
    const overwrites = [
      endScreenConfig && overwriteExistingEndscreen,
      infocardsConfig && overwriteExistingInfocards,
    ].filter(value => value !== undefined)
    const overwriteSentence = overwrites.every(Boolean)
      ? t('pages.videoPromotion.confirmation.launchConfirmOverwrite')
      : overwrites.every(value => !value)
        ? t('pages.videoPromotion.confirmation.launchConfirmSkip')
        : t('pages.videoPromotion.confirmation.launchConfirmMixed', {
            endscreen: t(
              `pages.videoPromotion.confirmation.${
                overwriteExistingEndscreen ? 'overwritten' : 'skipped'
              }`,
            ),
            infocards: t(
              `pages.videoPromotion.confirmation.${
                overwriteExistingInfocards ? 'overwritten' : 'skipped'
              }`,
            ),
          })
    modals.openConfirmModal({
      title: <Text fw="bold">{t('pages.videoPromotion.confirmation.launchConfirmTitle')}</Text>,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            {t('pages.videoPromotion.confirmation.launchConfirm', {
              elements,
              smart_count: videoPromotionStore.youtubeVideoIds.length,
              channel: youtubeChannelQuery.data?.name ?? videoPromotionStore.youtubeChannelId,
            })}
          </Text>
          <Text size="sm">{overwriteSentence}</Text>
        </Stack>
      ),
      labels: {
        confirm: t('pages.videoPromotion.confirmation.launch'),
        cancel: t('_generics.cancel'),
      },
      cancelProps: { variant: 'subtle' },
      onConfirm: onLaunch,
    })
  }

  const onLaunch = async () => {
    if (!videoPromotionStore.youtubeChannelId || videoPromotionStore.youtubeVideoIds.length === 0) {
      return
    }
    try {
      const { body } = await createOperationMutation.mutateAsync({
        data: {
          config: {
            endscreen: endScreenConfig,
            infocards: infocardsConfig,
            overwriteExistingEndscreen,
            overwriteExistingInfocards,
          },
          name: name || undefined,
          videoIds: videoPromotionStore.youtubeVideoIds,
          youtubeChannelId: videoPromotionStore.youtubeChannelId,
        },
      })
      videoPromotionStore.reset()
      notify('pages.videoPromotion.confirmation.success', { type: 'success' })
      navigate(`/videosPromotions/${body.operation.id}`)
    } catch (e: unknown) {
      const message =
        (e as AxiosError)?.response?.data.message || (e as AxiosError)?.response?.data || e
      notify(message, { type: 'error' })
    }
  }

  const isNextEnabled = useMemo(() => {
    switch (active) {
      case 0:
        return videoPromotionStore.youtubeVideoIds.length > 0

      case 1: {
        const { endScreen, infoCards } = videoPromotionStore.elementsConfiguration
        if (!endScreen.active && !infoCards.active) {
          return false
        }
        return (!endScreen.active || !!endScreenConfig) && (!infoCards.active || !!infocardsConfig)
      }

      default:
        return false
    }
  }, [
    active,
    videoPromotionStore.elementsConfiguration,
    videoPromotionStore.youtubeVideoIds,
    endScreenConfig,
    infocardsConfig,
  ])

  return (
    <Stack w="100%">
      <Box pos="sticky" top={APP_BAR_HEIGHT} bg="white" py="sm" style={{ zIndex: 10 }}>
        <Flex direction="row" justify="space-between" align="center" wrap="wrap" mb="md">
          <Group justify="end">
            <Title order={1} size="h2">
              {t('pages.videoPromotion.newOperation')}
            </Title>
            <Button
              type="button"
              onClick={() => navigate('/videosPromotions')}
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowNarrowLeft size={16} />}
            >
              <Text size="sm" tt="uppercase">
                {t('_generics.backToList')}
              </Text>
            </Button>
          </Group>
          <Group justify="center">
            {active > 0 && (
              <Button variant="subtle" size="compact-md" onClick={prevStep}>
                {t('_generics.previous')}
              </Button>
            )}
            {active < 2 && (
              <Tooltip
                label={t(`pages.videoPromotion.operationStepsTooltips.${active}`)}
                hidden={isNextEnabled}
              >
                <Button onClick={nextStep} size="compact-md" disabled={!isNextEnabled}>
                  {t('_generics.next')}
                </Button>
              </Tooltip>
            )}
            {active === 2 && (
              <Button
                size="compact-md"
                onClick={onConfirmLaunch}
                loading={createOperationMutation.isLoading}
                disabled={!endScreenConfig && !infocardsConfig}
              >
                {t('pages.videoPromotion.confirmation.launch')}
              </Button>
            )}
          </Group>
        </Flex>
        <Stepper size="md" iconSize={32} active={active} onStepClick={setActive}>
          <Stepper.Step label={t('pages.videoPromotion.operationSteps.0')} allowStepClick={false} />
          <Stepper.Step label={t('pages.videoPromotion.operationSteps.1')} allowStepClick={false} />
          <Stepper.Step label={t('pages.videoPromotion.operationSteps.2')} allowStepClick={false} />
        </Stepper>
      </Box>
      {active === 0 && <NewPromotionVideosSelect />}
      {active === 1 && <NewPromotionElementsConfiguration />}
      {active === 2 && (
        <NewPromotionConfirmation
          name={name}
          onNameChange={setName}
          overwriteExistingEndscreen={overwriteExistingEndscreen}
          onOverwriteExistingEndscreenChange={setOverwriteExistingEndscreen}
          overwriteExistingInfocards={overwriteExistingInfocards}
          onOverwriteExistingInfocardsChange={setOverwriteExistingInfocards}
        />
      )}
    </Stack>
  )
}
